import { useDialog } from 'muibox';
import qs from 'qs';
import { useContext, useState } from 'react';
import { InfiniteData, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';
import { VaultMediaResponse, VaultMediaResponseList, VaultMediaSettings } from './useSubscriberVaultMedias';

const useSubscribersVaultMediasAdditional = () => {
    const settingsContext = useContext(SettingsContext);
    const params = useParams();
    const axios = useAxios();
    const dialog = useDialog();
    const qc = useQueryClient();

    const [isInventoryVaultItemsLoading, setIsInventoryVaultItemsLoading] = useState(false);

    /**
     * getManyVaultMediaFromDatabase - Temporarily needed to pull vault media items for an inventory item.
     * @param mediaIds
     * @returns VaultMediaResponseList[]
     */
    const getManyVaultMediaFromDatabase = async (mediaIds: number[]): Promise<VaultMediaResponseList[]> => {
        setIsInventoryVaultItemsLoading(true);

        const query: string = `${settingsContext.routes.onlyFans.base}${params.userId}/vaultMediasItems`;

        return axios
            .get(query, {
                params: {
                    mediaIds: mediaIds,
                },
                paramsSerializer: params => {
                    return qs.stringify(params, { arrayFormat: 'repeat' });
                },
            })
            .then(response => {
                setIsInventoryVaultItemsLoading(false);
                return response.data;
            })
            .catch(error => {
                setIsInventoryVaultItemsLoading(false);
                console.error(error);
            });
    };

    /**
     * updateVaultMediaSettings - Updates a specific vaultItems settings
     * @param mediaId
     * @param newSettings
     * @returns a boolean of true for success or false for no success
     */
    const updateVaultMediaSettings = async (mediaId: number, newSettings: VaultMediaSettings): Promise<boolean> => {
        const query: string = `${settingsContext.routes.onlyFans.base}${params.userId}/updateMediaSettings/${mediaId}`;
        return axios
            .post(query, newSettings)
            .then(response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);
            });
    };

    /**
     * Update a single media item by id using an updater function
     * (handy for nested fields like settings/title).
     */
    // const updateVaultMediaInCache = (id: number, updater: (curr: VaultMediaResponseList) => VaultMediaResponseList) => {
    //     const curr = qc.getQueryData<InfiniteData<VaultMediaResponse>>(queryKey);
    //     if (!curr) return; // nothing cached yet

    //     let touched = false;
    //     const pages = curr.pages.map(page => {
    //         const idx = page.list.findIndex(m => m.id === id);
    //         if (idx === -1) return page;

    //         touched = true;
    //         const list = page.list.slice();
    //         list[idx] = updater(list[idx]);
    //         return { ...page, list };
    //     });

    //     if (!touched) return;
    //     qc.setQueryData<InfiniteData<VaultMediaResponse>>(queryKey, { ...curr, pages });
    // };

    const updateVaultMediaInCache = (id: number, updater: (curr: VaultMediaResponseList) => VaultMediaResponseList) => {
        qc.setQueriesData<InfiniteData<VaultMediaResponse> | undefined>(
            {
                queryKey: ['vaultMedias'],
                exact: false, // match all keys that start with ['vaultMedias', ...]
            },
            curr => {
                if (!curr) return curr; // nothing cached for this query

                let touched = false;

                const pages = curr.pages.map(page => {
                    const idx = page.list.findIndex(m => m.id === id);
                    if (idx === -1) return page;

                    touched = true;
                    const list = page.list.slice();
                    list[idx] = updater(list[idx]);
                    return { ...page, list };
                });

                // If this particular cache didn’t contain the media, leave it unchanged
                if (!touched) return curr;

                return { ...curr, pages };
            },
        );
    };

    return {
        getManyVaultMediaFromDatabase,
        isInventoryVaultItemsLoading,
        updateVaultMediaSettings,
        updateVaultMediaInCache,
    };
};

export default useSubscribersVaultMediasAdditional;
